import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  FormControl,
  Select,
  InputLabel,
  Menu,
  ListItemIcon,
  ListItemText,
} from "@material-ui/core";
import {
  DeleteOutline,
  FileCopy,
  Cloud,
  PowerSettingsNew,
  Refresh,
  Settings,
  Subject,
  MoreVert,
  SystemUpdateAlt,
} from "@material-ui/icons";
import Chip from "@material-ui/core/Chip";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import AddLinkIcon from "@mui/icons-material/AddLink";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import ChannelIcon, {
  useChannelPlugins,
  clearPluginsCache,
} from "../../components/ChannelIcon";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import PluginSettingsForm from "../../components/PluginSettingsForm";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  pluginGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: 16,
    marginTop: 16,
  },
  pluginCard: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  pluginHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
    paddingRight: 32, // espaço pro botão de ações (kebab) absoluto no canto
  },
  pluginInfo: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  capabilities: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    marginTop: 4,
  },
  capChip: {
    height: 20,
    fontSize: 11,
  },
  typeChip: {
    height: 24,
    fontSize: 11,
  },
  pluginActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 4,
    marginTop: "auto",
    paddingTop: 8,
    borderTop: "1px solid #eee",
  },
  baseUrl: {
    fontSize: 11,
    color: "#888",
    marginTop: 2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 200,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6),
  },
  "@global": {
    "@keyframes spin": {
      "0%": { transform: "rotate(0deg)" },
      "100%": { transform: "rotate(360deg)" },
    },
  },
}));

const channelCapLabels = {
  sendText: "Texto",
  sendMedia: "Midia",
  sendReaction: "Reacoes",
  groups: "Grupos",
  editMessage: "Editar",
  deleteMessage: "Deletar",
};

const providesLabels = {
  channel: { label: "Canal", color: "#2196f3" },
  hooks: { label: "Hooks", color: "#ff9800" },
  automationSteps: { label: "Automacao", color: "#9c27b0" },
  jobs: { label: "Jobs", color: "#4caf50" },
  panels: { label: "Painel", color: "#e91e63" },
  routes: { label: "Rotas", color: "#607d8b" },
  actions: { label: "Acoes", color: "#00bcd4" },
};

/**
 * Deriva o motivo de bloqueio/aviso de compat a partir dos três gates que o
 * backend já calcula (POST /plugins/preview e GET /plugins retornam o mesmo
 * shape): sdkCompat (SDK do plugin atrás do que o backend exige), backendCompat
 * (backend atrás do que o plugin exige) e sdkRangeCompat (SDK fora da faixa
 * que o próprio plugin declarou). Cada gate só existe no objeto se o campo
 * correspondente foi declarado — retrocompat: plugin antigo sem nenhum desses
 * não bloqueia/avisa. Retorna null ou uma string com o motivo.
 *
 * `context`: "register" (modal de cadastro, ainda vai registrar) ou "card"
 * (plugin já cadastrado, listagem) — só muda o verbo final da mensagem.
 */
function getPluginCompatBlockReason(plugin, context = "register") {
  if (!plugin) return null;
  const cta = i18n.t(
    context === "register" ? "plugins.compat.ctaRegister" : "plugins.compat.ctaRestore"
  );
  if (plugin.sdkCompat?.sdkCompatible === false) {
    const versionSuffix = plugin.sdkCompat.sdkVersion
      ? i18n.t("plugins.compat.versionSuffix", { version: plugin.sdkCompat.sdkVersion })
      : "";
    const minSuffix = plugin.sdkCompat.backendMinSdkVersion
      ? i18n.t("plugins.compat.sdkOutdatedMinSuffix", { min: plugin.sdkCompat.backendMinSdkVersion })
      : "";
    return i18n.t("plugins.compat.sdkOutdated", { versionSuffix, minSuffix, cta });
  }
  if (plugin.backendCompat?.backendCompatible === false) {
    return i18n.t("plugins.compat.backendOutdated", {
      minBackendVersion: plugin.backendCompat.minBackendVersion,
      backendVersion: plugin.backendCompat.backendVersion,
      cta
    });
  }
  if (plugin.sdkRangeCompat?.inRange === false) {
    const { sdkVersion, minSdkVersion, maxSdkVersion } = plugin.sdkRangeCompat;
    const range = [
      minSdkVersion ? `>= ${minSdkVersion}` : null,
      maxSdkVersion ? `<= ${maxSdkVersion}` : null,
    ].filter(Boolean).join(` ${i18n.t("plugins.compat.rangeAnd")} `);
    return i18n.t("plugins.compat.sdkOutOfRange", { sdkVersion, range, cta });
  }
  return null;
}

const Plugins = () => {
  const classes = useStyles();
  const plugins = useChannelPlugins();
  // Ao abrir a página, invalida o cache e refetcha o /plugins — o badge de
  // status (online/offline do transporte) reflete o estado atual do backend.
  useEffect(() => {
    clearPluginsCache();
  }, []);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [pluginToRemove, setPluginToRemove] = useState(null);
  // Menu de ações (kebab) por plugin: âncora + plugin atual.
  const [actionsMenu, setActionsMenu] = useState({ anchor: null, plugin: null });
  const openActionsMenu = (e, plugin) => setActionsMenu({ anchor: e.currentTarget, plugin });
  const closeActionsMenu = () => setActionsMenu({ anchor: null, plugin: null });
  const [remoteUrl, setRemoteUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [generatedApiKey, setGeneratedApiKey] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [settingsPlugin, setSettingsPlugin] = useState(null);
  const [settingsValues, setSettingsValues] = useState({});
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [reloadingPluginId, setReloadingPluginId] = useState(null);
  const [logsPlugin, setLogsPlugin] = useState(null);
  const [logsData, setLogsData] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsLevel, setLogsLevel] = useState("");
  const [logsLimit, setLogsLimit] = useState(200);
  const [logsSearch, setLogsSearch] = useState("");

  // Buscar metadata ao digitar URL (debounce 800ms) — via backend para evitar CORS
  useEffect(() => {
    setPreview(null);
    setPreviewError("");

    if (!remoteUrl || !remoteUrl.startsWith("http")) return;

    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const { data } = await api.post("/plugins/preview", {
          baseUrl: remoteUrl,
        });
        setPreview(data);
        setPreviewError("");
      } catch (err) {
        setPreview(null);
        const msg =
          err?.response?.data?.error || "Nao foi possivel conectar ao servidor";
        setPreviewError(msg);
      } finally {
        setPreviewLoading(false);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [remoteUrl]);

  const handleAddPlugin = async () => {
    if (!remoteUrl) {
      toast.error("URL do servidor e obrigatoria");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/plugins/remote", {
        baseUrl: remoteUrl,
      });
      toast.success(`Plugin "${data.metadata.displayName}" registrado!`);
      clearPluginsCache();

      // Backend gera a apiKey — mostra pra copiar.
      if (data.apiKey) {
        setGeneratedApiKey(data.apiKey);
      } else {
        handleCloseAddModal();
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
    setRemoteUrl("");
    setGeneratedApiKey("");
    setPreview(null);
    setPreviewError("");
  };

  const handleOpenSettings = async (plugin) => {
    setSettingsPlugin(plugin);
    setSettingsLoading(true);
    try {
      const { data } = await api.get(`/plugins/${plugin.id}/settings`);
      // Preencher com defaults dos fields que nao tem valor
      const defaults = {};
      (plugin.provides?.settings || []).forEach((f) => {
        if (f.defaultValue !== undefined) defaults[f.name] = f.defaultValue;
      });
      setSettingsValues({ ...defaults, ...data });
    } catch {
      setSettingsValues({});
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!settingsPlugin) return;
    setSettingsLoading(true);
    try {
      await api.put(`/plugins/${settingsPlugin.id}/settings`, settingsValues);
      toast.success("Configuracoes salvas");
      setSettingsPlugin(null);
    } catch (err) {
      toastError(err);
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleReloadPlugin = async (plugin) => {
    const pluginId = plugin.id || plugin.channel;
    if (!pluginId) return;
    setReloadingPluginId(pluginId);
    try {
      const { data } = await api.post(`/plugins/${pluginId}/reload`);
      toast.success(
        `Plugin ${plugin.displayName || pluginId} recarregado` +
        (data?.version ? ` (v${data.version})` : "") +
        (data?.hooksCount != null ? ` · ${data.hooksCount} hooks` : "")
      );
      clearPluginsCache();
    } catch (err) {
      toastError(err);
    } finally {
      setReloadingPluginId(null);
    }
  };

  const handleReprovisionPlugin = async (plugin) => {
    const pluginId = plugin.id || plugin.channel;
    if (!pluginId) return;
    setReloadingPluginId(pluginId);
    try {
      const { data } = await api.post(`/plugins/${pluginId}/reprovision`);
      toast.success(
        `Plugin ${plugin.displayName || pluginId} reprovisionado` +
        (data?.restarted ? ` · ${data.restarted} conexao(oes) reiniciada(s)` : "")
      );
      clearPluginsCache();
    } catch (err) {
      toastError(err);
    } finally {
      setReloadingPluginId(null);
    }
  };

  const fetchLogs = async (plugin, level, limit) => {
    const pluginId = plugin.id || plugin.channel;
    if (!pluginId) return;
    setLogsLoading(true);
    try {
      const params = { limit: limit || logsLimit };
      if (level) params.level = level;
      const { data } = await api.get(`/plugins/${pluginId}/logs`, { params });
      setLogsData(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
      setLogsData([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleOpenLogs = (plugin) => {
    setLogsPlugin(plugin);
    setLogsLevel("");
    setLogsSearch("");
    setLogsLimit(200);
    fetchLogs(plugin, "", 200);
  };

  const handleRemovePlugin = async (pluginId) => {
    if (!pluginId) {
      toast.error("Plugin sem identificador");
      return;
    }
    try {
      await api.delete(`/plugins/${pluginId}`);
      toast.success("Plugin removido");
      clearPluginsCache();
      setPluginToRemove(null);
    } catch (err) {
      toastError(err);
    }
  };

  const handleCopyApiKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.info("API Key copiada!");
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Plugins ({plugins.length})</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="default"
            onClick={() => clearPluginsCache()}
            startIcon={<Refresh />}
          >
            Atualizar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setAddModalOpen(true)}
            startIcon={<AddLinkIcon />}
          >
            Adicionar Plugin Remoto
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {plugins.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              Nenhum plugin instalado
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Plugins locais (Telegram, Discord) sao carregados automaticamente
              ao iniciar o servidor.
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Para adicionar um plugin remoto, clique em "Adicionar Plugin
              Remoto".
            </Typography>
          </div>
        ) : (
          <div className={classes.pluginGrid}>
            {plugins.map((plugin) => (
              <Card key={plugin.channel} variant="outlined" style={{ position: "relative" }}>
                {(plugin.isRemote || plugin.apiKey || plugin.supportsLogs) && (
                  <Tooltip title="Ações">
                    <IconButton
                      size="small"
                      onClick={(e) => openActionsMenu(e, plugin)}
                      style={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <CardContent className={classes.pluginCard}>
                  <div className={classes.pluginHeader}>
                    <div className={classes.pluginInfo}>
                      <ChannelIcon
                        channel={plugin.channel}
                        style={{ fontSize: 32 }}
                      />
                      <div>
                        <Typography
                          variant="subtitle1"
                          style={{ fontWeight: 600, lineHeight: 1.2, display: "flex", alignItems: "center", gap: 6 }}
                        >
                          {plugin.displayName}
                          {plugin.isRemote && (
                            <span
                              title={plugin.offline ? "Backend sem conexão com o plugin" : "Conectado ao plugin"}
                              style={{
                                display: "inline-flex", alignItems: "center", gap: 4,
                                fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 10,
                                color: plugin.offline ? "#b71c1c" : "#1b5e20",
                                background: plugin.offline ? "#ffebee" : "#e8f5e9",
                              }}
                            >
                              <span style={{
                                width: 7, height: 7, borderRadius: "50%",
                                background: plugin.offline ? "#e53935" : "#43a047",
                              }} />
                              {plugin.offline ? "offline" : "online"}
                            </span>
                          )}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          {plugin.id || plugin.channel}
                          {plugin.version && ` v${plugin.version}`}
                          {(() => {
                            const reason = getPluginCompatBlockReason(plugin, "card");
                            return reason && (
                              <Tooltip title={reason}>
                                <SystemUpdateAlt style={{ fontSize: 13, color: "#f57c00" }} />
                              </Tooltip>
                            );
                          })()}
                        </Typography>
                        {plugin.description && (
                          <Typography variant="body2" color="textSecondary" style={{ fontSize: 11 }}>
                            {plugin.description}
                          </Typography>
                        )}
                        {plugin.baseUrl && (
                          <Tooltip title={plugin.baseUrl}>
                            <Typography className={classes.baseUrl}>
                              {plugin.baseUrl}
                            </Typography>
                          </Tooltip>
                        )}
                        {plugin.isRemote && plugin.offline && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            disabled={reloadingPluginId === (plugin.id || plugin.channel)}
                            onClick={() => handleReloadPlugin(plugin)}
                            style={{ marginTop: 6, textTransform: "none", fontSize: 11, padding: "1px 10px" }}
                          >
                            {reloadingPluginId === (plugin.id || plugin.channel)
                              ? "Reconectando…"
                              : "Reconectar plugin"}
                          </Button>
                        )}
                      </div>
                    </div>
                    <Chip
                      size="small"
                      icon={
                        plugin.isRemote ? (
                          <Cloud style={{ fontSize: 14 }} />
                        ) : (
                          <PowerSettingsNew style={{ fontSize: 14 }} />
                        )
                      }
                      label={plugin.isRemote ? "Remoto" : "Local"}
                      variant="outlined"
                      className={classes.typeChip}
                      color={plugin.isRemote ? "primary" : "default"}
                    />
                    {plugin.offline && (
                      <Chip
                        size="small"
                        label="Offline"
                        variant="outlined"
                        className={classes.typeChip}
                        style={{ color: "#f44336", borderColor: "#f44336" }}
                      />
                    )}
                  </div>

                  {/* Capacidades do plugin (provides) */}
                  <div className={classes.capabilities}>
                    {plugin.provides && Object.entries(providesLabels).map(
                      ([key, { label, color }]) => {
                        const val = plugin.provides[key];
                        const hasIt = val === true || (Array.isArray(val) && val.length > 0) || (typeof val === "object" && !Array.isArray(val) && val);
                        return hasIt ? (
                          <Chip
                            key={key}
                            size="small"
                            label={label}
                            className={classes.capChip}
                            style={{ backgroundColor: color, color: "#fff" }}
                          />
                        ) : null;
                      }
                    )}
                  </div>

                  {/* Capabilities de canal (se tem canal) */}
                  {plugin.provides?.channel && (
                    <div className={classes.capabilities} style={{ marginTop: 4 }}>
                      {Object.entries(channelCapLabels).map(
                        ([key, label]) =>
                          plugin.capabilities?.[key] && (
                            <Chip
                              key={key}
                              size="small"
                              label={label}
                              className={classes.capChip}
                            />
                          )
                      )}
                    </div>
                  )}

                  {/* Ação primária no rodapé; demais ações no menu "⋮" do topo. */}
                  {plugin.provides?.settings?.length > 0 && (
                    <div className={classes.pluginActions}>
                      <Button
                        size="small"
                        color="primary"
                        startIcon={<Settings style={{ fontSize: 16 }} />}
                        onClick={() => handleOpenSettings(plugin)}
                        style={{ textTransform: "none", fontSize: 12 }}
                      >
                        Configurar
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </Paper>

      {/* Menu de ações do plugin (kebab) */}
      <Menu
        anchorEl={actionsMenu.anchor}
        open={Boolean(actionsMenu.anchor)}
        onClose={closeActionsMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {actionsMenu.plugin?.apiKey && (
          <MenuItem
            onClick={() => {
              handleCopyApiKey(actionsMenu.plugin.apiKey);
              closeActionsMenu();
            }}
          >
            <ListItemIcon style={{ minWidth: 32 }}><FileCopy fontSize="small" /></ListItemIcon>
            <ListItemText primary="Copiar API Key" />
          </MenuItem>
        )}
        {actionsMenu.plugin?.supportsLogs && (
          <MenuItem
            onClick={() => {
              handleOpenLogs(actionsMenu.plugin);
              closeActionsMenu();
            }}
          >
            <ListItemIcon style={{ minWidth: 32 }}><Subject fontSize="small" /></ListItemIcon>
            <ListItemText primary="Logs" />
          </MenuItem>
        )}
        {actionsMenu.plugin?.isRemote && (
          <MenuItem
            onClick={() => {
              handleReloadPlugin(actionsMenu.plugin);
              closeActionsMenu();
            }}
          >
            <ListItemIcon style={{ minWidth: 32 }}><Refresh fontSize="small" /></ListItemIcon>
            <ListItemText primary="Recarregar metadata" secondary="sem reiniciar" />
          </MenuItem>
        )}
        {actionsMenu.plugin?.isRemote && (
          <MenuItem
            onClick={() => {
              handleReprovisionPlugin(actionsMenu.plugin);
              closeActionsMenu();
            }}
          >
            <ListItemIcon style={{ minWidth: 32 }}><PowerSettingsNew fontSize="small" /></ListItemIcon>
            <ListItemText primary="Reprovisionar" secondary="resolve 503 e reinicia conexões" />
          </MenuItem>
        )}
        {actionsMenu.plugin?.isRemote && (
          <MenuItem
            onClick={() => {
              setPluginToRemove(actionsMenu.plugin);
              closeActionsMenu();
            }}
          >
            <ListItemIcon style={{ minWidth: 32 }}><DeleteOutline fontSize="small" color="error" /></ListItemIcon>
            <ListItemText primary="Remover" primaryTypographyProps={{ color: "error" }} />
          </MenuItem>
        )}
      </Menu>

      {/* Modal de adicionar plugin remoto */}
      {(() => {
        const compatBlockReason = getPluginCompatBlockReason(preview);
        return (
      <Dialog
        open={addModalOpen}
        onClose={handleCloseAddModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Adicionar Plugin Remoto</DialogTitle>
        <DialogContent>
          {generatedApiKey ? (
            <>
              <Typography
                variant="body1"
                gutterBottom
                style={{ marginBottom: 16 }}
              >
                Plugin registrado! Configure esta API Key no servidor remoto
                (variavel <code>API_KEY</code>):
              </Typography>
              <TextField
                fullWidth
                value={generatedApiKey}
                InputProps={{ readOnly: true }}
                variant="outlined"
                onClick={() => handleCopyApiKey(generatedApiKey)}
                helperText="Clique para copiar"
              />
            </>
          ) : (
            <>
              <TextField
                autoFocus
                margin="dense"
                label="URL do Servidor"
                placeholder="http://localhost:3033"
                fullWidth
                value={remoteUrl}
                onChange={(e) => setRemoteUrl(e.target.value)}
                helperText={
                  previewError || "URL base do servidor que roda o plugin (ex: http://servidor:3033)"
                }
                error={!!previewError}
                InputProps={{
                  endAdornment: previewLoading ? (
                    <CircularProgress size={20} />
                  ) : null,
                }}
              />

              {preview && (
                <Card
                  variant="outlined"
                  style={{
                    marginTop: 12,
                    marginBottom: 8,
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <CardContent style={{ padding: "12px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                      <ChannelIcon
                        channel={preview.id || preview.channel}
                        style={{ fontSize: 28 }}
                      />
                      <div>
                        <Typography variant="subtitle1" style={{ fontWeight: 600, lineHeight: 1.2 }}>
                          {preview.displayName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {preview.id || preview.channel}
                          {preview.version && ` v${preview.version}`}
                        </Typography>
                        {preview.description && (
                          <Typography variant="body2" color="textSecondary" style={{ fontSize: 12, marginTop: 2 }}>
                            {preview.description}
                          </Typography>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {/* Provides (formato novo) */}
                      {preview.provides && Object.entries(providesLabels).map(
                        ([key, { label, color }]) => {
                          const val = preview.provides[key];
                          const hasIt = val === true || (Array.isArray(val) && val.length > 0) || (typeof val === "object" && !Array.isArray(val) && val);
                          return hasIt ? (
                            <Chip key={key} size="small" label={label} className={classes.capChip} style={{ backgroundColor: color, color: "#fff" }} />
                          ) : null;
                        }
                      )}
                      {/* Fallback formato legado */}
                      {!preview.provides && preview.capabilities && Object.entries(channelCapLabels).map(
                        ([key, label]) =>
                          preview.capabilities?.[key] && (
                            <Chip key={key} size="small" label={label} className={classes.capChip} />
                          )
                      )}
                    </div>
                    {compatBlockReason && (
                      <div
                        style={{
                          display: "flex", alignItems: "flex-start", gap: 8,
                          marginTop: 10, padding: "8px 10px", borderRadius: 6,
                          backgroundColor: "#fff3e0", border: "1px solid #ffb74d",
                        }}
                      >
                        <SystemUpdateAlt style={{ fontSize: 18, color: "#e65100", flexShrink: 0, marginTop: 1 }} />
                        <Typography variant="body2" style={{ fontSize: 12, color: "#e65100" }}>
                          {compatBlockReason}
                        </Typography>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddModal}>
            {generatedApiKey ? "Fechar" : "Cancelar"}
          </Button>
          {!generatedApiKey && (
            <Tooltip title={compatBlockReason || ""}>
              <span>
                <Button
                  onClick={handleAddPlugin}
                  color="primary"
                  variant="contained"
                  disabled={loading || !remoteUrl || !!compatBlockReason}
                >
                  {loading ? <CircularProgress size={24} /> : "Registrar"}
                </Button>
              </span>
            </Tooltip>
          )}
        </DialogActions>
      </Dialog>
        );
      })()}

      {/* Confirmacao de remocao */}
      <ConfirmationModal
        title="Remover Plugin"
        open={!!pluginToRemove}
        onClose={() => setPluginToRemove(null)}
        onConfirm={() => handleRemovePlugin(pluginToRemove?.id || pluginToRemove?.channel)}
      >
        Deseja remover o plugin{" "}
        <strong>{pluginToRemove?.displayName}</strong>? As conexoes que usam
        este plugin serao desconectadas.
      </ConfirmationModal>

      {/* Modal de configuracao do plugin */}
      {(() => {
        const layout = settingsPlugin?.provides?.settingsLayout || "md";
        const isFullscreen = layout === "fullscreen";
        const dialogProps = isFullscreen
          ? { fullScreen: true }
          : { maxWidth: layout, fullWidth: true };
        return (
      <Dialog
        open={!!settingsPlugin}
        onClose={() => setSettingsPlugin(null)}
        {...dialogProps}
      >
        <DialogTitle>
          Configurar {settingsPlugin?.displayName}
        </DialogTitle>
        <DialogContent>
          {settingsLoading ? (
            <div style={{ textAlign: "center", padding: 24 }}>
              <CircularProgress />
            </div>
          ) : (
            <PluginSettingsForm
              fields={settingsPlugin?.provides?.settings || []}
              tabs={settingsPlugin?.provides?.settingsTabs || []}
              values={settingsValues}
              onChange={setSettingsValues}
              pluginId={settingsPlugin?.id || settingsPlugin?.channel}
              routePath={settingsPlugin?.routePath}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsPlugin(null)}>Cancelar</Button>
          <Button
            onClick={handleSaveSettings}
            color="primary"
            variant="contained"
            disabled={settingsLoading}
          >
            {settingsLoading ? <CircularProgress size={24} /> : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
        );
      })()}

      <Dialog
        open={!!logsPlugin}
        onClose={() => setLogsPlugin(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Logs · {logsPlugin?.displayName || logsPlugin?.id}
        </DialogTitle>
        <DialogContent dividers>
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
            <TextField
              variant="outlined"
              size="small"
              label="Buscar"
              placeholder="Filtrar texto..."
              value={logsSearch}
              onChange={(e) => setLogsSearch(e.target.value)}
              style={{ minWidth: 200, flex: 1 }}
            />
            <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
              <InputLabel>Nível</InputLabel>
              <Select
                label="Nível"
                value={logsLevel}
                onChange={(e) => {
                  setLogsLevel(e.target.value);
                  if (logsPlugin) fetchLogs(logsPlugin, e.target.value, logsLimit);
                }}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="debug">Debug+</MenuItem>
                <MenuItem value="info">Info+</MenuItem>
                <MenuItem value="warn">Warn+</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" style={{ minWidth: 110 }}>
              <InputLabel>Limite</InputLabel>
              <Select
                label="Limite"
                value={logsLimit}
                onChange={(e) => {
                  setLogsLimit(e.target.value);
                  if (logsPlugin) fetchLogs(logsPlugin, logsLevel, e.target.value);
                }}
              >
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={200}>200</MenuItem>
                <MenuItem value={500}>500</MenuItem>
              </Select>
            </FormControl>
            <Button
              size="small"
              startIcon={<Refresh />}
              onClick={() => logsPlugin && fetchLogs(logsPlugin, logsLevel, logsLimit)}
              disabled={logsLoading}
            >
              Atualizar
            </Button>
          </div>

          <div
            style={{
              fontFamily: "monospace",
              fontSize: 12,
              background: "#1e1e1e",
              color: "#d4d4d4",
              borderRadius: 4,
              padding: 12,
              maxHeight: 420,
              overflow: "auto",
              whiteSpace: "pre-wrap",
            }}
          >
            {(() => {
              const term = logsSearch.trim().toLowerCase();
              const filtered = term
                ? logsData.filter((e) =>
                    (e.message || "").toLowerCase().includes(term)
                  )
                : logsData;
              if (logsLoading) {
                return (
                  <div style={{ textAlign: "center", padding: 20 }}>
                    <CircularProgress size={24} />
                  </div>
                );
              }
              if (filtered.length === 0) {
                return (
                  <div style={{ color: "#888" }}>
                    {term ? "Nenhum log corresponde ao filtro." : "Sem logs disponíveis."}
                  </div>
                );
              }
              return filtered.map((entry, i) => {
                const color =
                  entry.level === "error" ? "#f48771"
                    : entry.level === "warn" ? "#dcdcaa"
                    : entry.level === "debug" ? "#888"
                    : "#9cdcfe";
                const ts = entry.timestamp
                  ? new Date(entry.timestamp).toLocaleTimeString()
                  : "";
                return (
                  <div key={i}>
                    <span style={{ color: "#666" }}>{ts}</span>{" "}
                    <span style={{ color, fontWeight: "bold" }}>
                      [{(entry.level || "info").toUpperCase()}]
                    </span>{" "}
                    {entry.connectionId != null && (
                      <span style={{ color: "#608b4e" }}>(conn {entry.connectionId}) </span>
                    )}
                    {entry.message}
                  </div>
                );
              });
            })()}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsPlugin(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Plugins;
